import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastActivity, setLastActivity] = useState(Date.now());

    // Auto-logout after 30 minutes of inactivity
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

    useEffect(() => {
        // Check for existing session
        const storedUser = localStorage.getItem('seelakari_admin_user');
        const storedToken = localStorage.getItem('seelakari_admin_token');
        const storedLastActivity = localStorage.getItem('seelakari_last_activity');

        if (storedUser && storedToken) {
            const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity) : Date.now();
            const timeSinceLastActivity = Date.now() - lastActivityTime;

            // Check if session has expired due to inactivity
            if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
                // Session expired, logout
                logout();
            } else {
                try {
                    setUser(JSON.parse(storedUser));
                    setLastActivity(lastActivityTime);
                } catch (error) {
                    console.error('Error loading user:', error);
                    localStorage.removeItem('seelakari_admin_user');
                    localStorage.removeItem('seelakari_admin_token');
                    localStorage.removeItem('seelakari_last_activity');
                }
            }
        }
        setLoading(false);
    }, []);

    // Track user activity
    useEffect(() => {
        if (!user) return;

        const updateActivity = () => {
            const now = Date.now();
            setLastActivity(now);
            localStorage.setItem('seelakari_last_activity', now.toString());
        };

        // Activity events
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        // Check for inactivity every minute
        const inactivityCheck = setInterval(() => {
            const timeSinceLastActivity = Date.now() - lastActivity;
            if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
                logout();
                alert('You have been logged out due to inactivity.');
            }
        }, 60000); // Check every minute

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
            clearInterval(inactivityCheck);
        };
    }, [user, lastActivity]);

    const login = async (email, password, totpToken = '') => {
        try {
            const response = await fetch('https://api.seelakariagro.com/api/admin/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, totpToken }),
            });

            const result = await response.json();

            if (result.success) {
                const now = Date.now();
                setUser(result.user);
                setLastActivity(now);
                localStorage.setItem('seelakari_admin_user', JSON.stringify(result.user));
                localStorage.setItem('seelakari_admin_token', result.token);
                localStorage.setItem('seelakari_last_activity', now.toString());
                return { success: true, user: result.user };
            } else {
                return {
                    success: false,
                    error: result.error || 'Invalid credentials',
                    requiresTOTP: result.requiresTOTP
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, error: 'Connection error. Please try again.' };
        }
    };

    const logout = () => {
        setUser(null);
        setLastActivity(Date.now());
        localStorage.removeItem('seelakari_admin_user');
        localStorage.removeItem('seelakari_admin_token');
        localStorage.removeItem('seelakari_last_activity');
    };

    const getToken = () => {
        return localStorage.getItem('seelakari_admin_token');
    };

    const value = {
        user,
        loading,
        login,
        logout,
        getToken,
        isAuthenticated: !!user,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
